import React from 'react';
import { motion } from 'framer-motion';
import { Bot, Zap, TrendingUp, ArrowRight } from 'lucide-react';

const AIInnovation = () => {
  const features = [
    {
      icon: Bot,
      title: 'AI-Powered Solutions',
      description: 'Leverage machine learning and AI to automate complex business processes'
    },
    {
      icon: Zap,
      title: 'Smart Automation',
      description: 'Reduce manual work with intelligent workflow automation systems'
    },
    {
      icon: TrendingUp,
      title: 'Predictive Analytics',
      description: 'Make data-driven decisions with advanced analytics and forecasting'
    }
  ];

  return (
    <section className="relative py-32 bg-[#121212] overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#00FFD1]/10 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00FFD1]/10 rounded-full blur-[120px] animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block text-[#00FFD1] text-lg font-medium mb-4 tracking-wide"
            >
              AI & INNOVATION
            </motion.span>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Intelligent Solutions for <span className="text-[#00FFD1]">Modern Business</span>
            </h2>
            <p className="text-xl text-white/85 mb-8 leading-relaxed">
              Transform your operations with cutting-edge AI technology. From chatbots to predictive 
              analytics, we build intelligent systems that give you a competitive edge.
            </p>

            {/* Features List */}
            <div className="space-y-6 mb-8">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex gap-4 items-start group cursor-pointer"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-[#00FFD1]/10 flex items-center justify-center transition-all duration-400 group-hover:bg-[#00FFD1]/20">
                      <IconComponent className="text-[#00FFD1]" size={24} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#00FFD1] transition-colors duration-400">
                        {feature.title}
                      </h3>
                      <p className="text-white/85 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="group bg-[#00FFD1] text-black px-8 py-4 text-lg font-medium transition-all duration-400 hover:bg-white hover:shadow-[0_0_30px_rgba(0,255,209,0.4)] flex items-center gap-3"
            >
              Explore AI Solutions
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" size={20} />
            </motion.button>
          </motion.div>

          {/* Right Content - AI Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative bg-black border border-white/25 p-8 aspect-square flex items-center justify-center overflow-hidden group">
              {/* Circuit Pattern Background */}
              <div className="absolute inset-0 opacity-20">
                <svg className="w-full h-full" viewBox="0 0 400 400">
                  <defs>
                    <pattern id="circuit" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                      <circle cx="2" cy="2" r="1" fill="#00FFD1" />
                      <path d="M2,2 L38,2" stroke="#00FFD1" strokeWidth="0.5" fill="none" />
                      <path d="M2,2 L2,38" stroke="#00FFD1" strokeWidth="0.5" fill="none" />
                    </pattern>
                  </defs>
                  <rect width="400" height="400" fill="url(#circuit)" />
                </svg>
              </div>

              {/* Central AI Icon */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative z-10"
              >
                <div className="w-48 h-48 bg-gradient-to-br from-[#00FFD1]/20 to-transparent border-2 border-[#00FFD1] flex items-center justify-center">
                  <Bot className="text-[#00FFD1]" size={96} strokeWidth={1} />
                </div>
              </motion.div>

              {/* Orbiting Elements */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    rotate: 360
                  }}
                  transition={{
                    duration: 10 + i * 5,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className="absolute inset-0"
                  style={{
                    transformOrigin: 'center'
                  }}
                >
                  <div 
                    className="absolute w-3 h-3 bg-[#00FFD1] rounded-full"
                    style={{
                      top: '50%',
                      left: `${50 + (i + 1) * 15}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  />
                </motion.div>
              ))}

              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#00FFD1]" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#00FFD1]" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#00FFD1]" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#00FFD1]" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AIInnovation;
